<script setup lang="ts">
/**
 * DetentSheet: the iOS-style sheet everything on phones presents in (the workspace, pickers, menus, forms).
 *
 * Detents ('peek' | 'medium' | 'large' | 'fit' | px), dragged from the grabber, the header, or the body when
 * it's scrolled to the top (scroll-to-drag handoff: pulling content down past its top moves the sheet; pushing
 * a shorter sheet up expands it first, then scrolls). Rubber-bands past the top, measures release velocity over
 * the last ~80ms, and a flick (≥ 0.5px/ms) moves one detent or dismisses. Snaps continue the finger's speed on
 * the iOS curve. The scrim follows the drag; at 'peek' there's none and the page stays usable.
 *
 * Built for zero layout thrash: at rest the panel is laid out at its detent height with no transform (a
 * transform makes it the containing block of `position: fixed` descendants, which broke menus inside the old
 * workspace sheet). While moving it is laid out once at the tallest height and only `transform` / `opacity`
 * change per frame. `will-change` is set only while moving.
 *
 * Also: Teleports to <body> on the --z-sheet / --z-sheet-modal layer, registers with the back stack (Android
 * back and Escape close it, or step down when it can't be dismissed), moves focus in and back out, traps Tab
 * while modal, lifts body and footer above the keyboard (and snaps to the top detent when a field inside
 * focuses), pads for the home indicator, and fades instead of sliding under reduced motion.
 * `presentation="auto"` renders a centred dialog card at ≥ 640px instead.
 *
 * Attributes (class, style) land on the panel. Style it with --ds-bg, --ds-radius, --ds-shadow.
 */
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { haptic } from '@/native/haptics'
import { pushBackHandler } from '@/native/backStack'
import { isTextEntry, keepAboveKeyboard, useKeyboardInset } from '@/native/keyboard'
import { topIslandHeight } from '@/native/topIsland'
import { DUR, DUR_REDUCED, EASE, prefersReducedMotion } from '@/motion/tokens'
import { isTouchPrimary } from '@/utils/platform'
import {
    OFFSCREEN_EXTRA,
    VelocityTracker,
    dragHeight,
    footerDrop,
    keyboardLift,
    pickSnap,
    resolveDetents,
    scrimOpacity,
    scrimRange,
    snapCurve,
    snapDuration,
    type Detent,
    type DetentId,
    type SheetMetrics,
} from './sheetMath'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
    open: boolean
    /** Resting heights, any order. A number is a px height. The sheet opens at the first (or `v-model:detent`). */
    detents?: DetentId[]
    /** 'sheet' (--z-sheet) or 'modal' (--z-sheet-modal, above other sheets). */
    layer?: 'sheet' | 'modal'
    title?: string
    /** Drag down, tap the scrim, back and Escape close it. When false they step down a detent instead. */
    dismissible?: boolean
    /** 'auto': none at peek, fading in up to the next detent. true: always (fades in from 0). */
    scrim?: 'auto' | boolean
    /** Lift the body and footer above the keyboard; a focused field snaps the sheet to its top detent. */
    keyboardAware?: boolean
    /** 'auto': a centred dialog card at ≥ 640px, a sheet below. */
    presentation?: 'sheet' | 'auto'
    ariaLabel?: string
    /** The grabber at the top. Tapping it cycles detents. */
    handle?: boolean
    bodyClass?: unknown
    /** The body doesn't scroll: the content fills it and scrolls itself (a pager, a live view). */
    noBodyScroll?: boolean
}>(), {
    detents: () => ['large'],
    layer: 'sheet',
    title: undefined,
    dismissible: true,
    scrim: 'auto',
    keyboardAware: true,
    presentation: 'sheet',
    ariaLabel: undefined,
    handle: true,
    bodyClass: undefined,
    noBodyScroll: false,
})

const emit = defineEmits<{
    /** The creator dismissed it (drag, scrim, back, Escape): set `open` to false. */
    close: []
    /** A snap finished settling at this detent. */
    snap: [detent: DetentId]
}>()

/** The current detent (two-way). Changing it while open animates there. */
const detentModel = defineModel<DetentId | undefined>('detent', { default: undefined })

const titleId = useId()
const isWide = useMediaQuery('(min-width: 640px)')
const isDialog = computed(() => props.presentation === 'auto' && isWide.value)
const keyboard = useKeyboardInset()

const rendered = ref(false)
const phase = ref<'closed' | 'entering' | 'open' | 'leaving'>('closed')
const dialogIn = ref(false)
const scrimActive = ref(false)
const dragging = ref(false)

const rootEl = ref<HTMLElement | null>(null)
const scrimEl = ref<HTMLElement | null>(null)
const panelEl = ref<HTMLElement | null>(null)
const headEl = ref<HTMLElement | null>(null)
const bodyEl = ref<HTMLElement | null>(null)
const contentEl = ref<HTMLElement | null>(null)
const spacerEl = ref<HTMLElement | null>(null)
const footEl = ref<HTMLElement | null>(null)
const probeEl = ref<HTMLElement | null>(null)

// ── geometry (plain variables: they change per frame and never render) ───────────────────────────────────
let metrics: SheetMetrics = { viewport: 0, safeTop: 0, safeBottom: 0, header: 0, fit: 0, keyboard: 0 }
/** The detents resolved to heights, shortest first. */
let stops: Detent[] = []
let index = 0
/** Panel layout height. */
let panelH = 0
/** Panel translateY; visible height = panelH - offset. */
let offset = 0
let footerH = 0
let lift = 0
let animToken = 0
let animating = false
let dismissVelocity = 0
let prevFocus: HTMLElement | null = null
let offBack: (() => void) | null = null
let ro: ResizeObserver | null = null
let relayoutFrame = 0
let pendingRelayout = false
let cancelReveal: (() => void) | null = null

interface Drag {
    startY: number
    startH: number
    /** Panel layout height for the whole drag (the top detent). */
    H: number
    fromBody: boolean
    scrollStart: number
    /** The drag has pushed past the top detent into scrolling the body. */
    scrolled: boolean
    tracker: VelocityTracker
}
let drag: Drag | null = null
let dragFrame = 0
let pendingY = 0
/** Header / grabber pointer, before and during a drag. */
let headPtr: { id: number; y: number; t: number; started: boolean } | null = null
/** Body touch: undecided, dragging the sheet, or left to native scrolling. */
let touch: { id: number; x: number; y: number; t: number; mode: 'pending' | 'drag' | 'scroll' } | null = null

const reduced = () => prefersReducedMotion()
const topIndex = () => stops.length - 1
const visibleH = () => panelH - offset

function measure() {
    const head = headEl.value
    const body = bodyEl.value
    const content = contentEl.value
    const probe = probeEl.value
    if (!head || !body || !content || !probe) return
    const ps = getComputedStyle(probe)
    const bs = getComputedStyle(body)
    const safeTop = parseFloat(ps.paddingTop) || 0
    const safeBottom = parseFloat(ps.paddingBottom) || 0
    footerH = footEl.value?.offsetHeight ?? 0
    const header = head.offsetHeight
    const pad = (parseFloat(bs.paddingTop) || 0) + (parseFloat(bs.paddingBottom) || 0)
    const viewport = window.innerHeight
    // A body that doesn't scroll has no natural height: 'fit' becomes 'large'.
    const fit = props.noBodyScroll ? viewport : header + pad + content.offsetHeight + (footerH || safeBottom)
    const kb = props.keyboardAware ? keyboard.height.value : 0
    metrics = { viewport, safeTop, safeBottom, header, fit, keyboard: kb }
    lift = keyboardLift(kb, safeBottom)
    stops = resolveDetents(props.detents, metrics)
    index = Math.min(Math.max(0, index), topIndex())
}

/** The spacer at the end of the body keeps content clear of the footer, the home indicator and the keyboard. */
function writeSpacer() {
    if (spacerEl.value) spacerEl.value.style.height = `${(footerH || metrics.safeBottom) + lift}px`
}

/** A data attribute, not a class: Vue rewrites className when the panel's bound class changes. */
function setMoving(on: boolean) {
    panelEl.value?.toggleAttribute('data-moving', on)
}

/** Write one frame: panel height (layout only when it changes), transform, scrim and footer. */
function writeFrame(H: number, off: number) {
    const p = panelEl.value
    if (!p) return
    if (H !== panelH) {
        p.style.height = `${H}px`
        panelH = H
    }
    offset = off
    p.style.transform = off === 0 ? '' : `translate3d(0, ${off}px, 0)`
    const h = H - off
    if (scrimEl.value) scrimEl.value.style.opacity = String(scrimOpacity(h, scrimRange(stops, props.scrim)))
    if (footEl.value) {
        const f = footerDrop(h, stops) - lift - off
        footEl.value.style.transform = f === 0 ? '' : `translate3d(0, ${f}px, 0)`
    }
}

/** Drop the inline geometry this component writes (and nothing a consumer passed as style). */
function clearInline() {
    for (const el of [panelEl.value, scrimEl.value, footEl.value]) {
        if (!el) continue
        for (const prop of ['height', 'transform', 'transition', 'opacity']) el.style.removeProperty(prop)
    }
}

function setTransition(t: string | null) {
    const v = (prop: string) => (t ? `${prop} ${t}` : 'none')
    if (panelEl.value) panelEl.value.style.transition = v('transform')
    if (scrimEl.value) scrimEl.value.style.transition = v('opacity')
    if (footEl.value) footEl.value.style.transition = v('transform')
}

/** Where the sheet visibly is right now, even mid-animation. */
function currentH(): number {
    const p = panelEl.value
    if (!p || !animating) return visibleH()
    const t = getComputedStyle(p).transform
    const ty = t && t !== 'none' ? new DOMMatrixReadOnly(t).m42 : 0
    return panelH - ty
}

/** Animate the visible height to `toH` (≤ 0 = off screen). Resolves false if another move took over. */
function animateTo(toH: number, duration: number, curve: string): Promise<boolean> {
    const p = panelEl.value
    if (!p) return Promise.resolve(false)
    const token = ++animToken
    const fromH = currentH()
    const H = toH > 0 ? Math.max(Math.round(fromH), toH) : Math.max(panelH, 1)
    setTransition(null)
    setMoving(true)
    writeFrame(H, H - fromH)
    void getComputedStyle(p).transform // commit the start so the transition runs from it
    animating = true
    setTransition(`${duration}ms ${curve}`)
    writeFrame(H, toH > 0 ? H - toH : H + OFFSCREEN_EXTRA)
    return new Promise((resolve) => {
        let timer: ReturnType<typeof setTimeout> | null = null
        const finish = () => {
            p.removeEventListener('transitionend', onEnd)
            if (timer) clearTimeout(timer)
            if (token !== animToken) return resolve(false)
            animating = false
            resolve(true)
        }
        const onEnd = (e: TransitionEvent) => {
            if (e.target === p && e.propertyName === 'transform') finish()
        }
        p.addEventListener('transitionend', onEnd)
        timer = setTimeout(finish, duration + 80)
    })
}

/** Back to rest at `h`: laid out at its height, no transform. */
function settle(h: number) {
    setTransition(null)
    writeFrame(h, 0)
    setMoving(false)
}

function interrupt() {
    if (!animating) return
    const h = currentH()
    animToken++
    animating = false
    setTransition(null)
    writeFrame(panelH, panelH - h)
}

function updateInteractivity(h: number) {
    scrimActive.value = scrimOpacity(h, scrimRange(stops, props.scrim)) > 0.01
}

function syncModel() {
    const id = stops[index]?.id
    if (id !== undefined && detentModel.value !== id) detentModel.value = id
}

async function snapToIndex(i: number, opts: { velocity?: number; user?: boolean; duration?: number } = {}) {
    if (!stops.length || isDialog.value) return
    const prev = index
    index = Math.min(Math.max(0, i), topIndex())
    if (index !== prev) {
        syncModel()
        if (opts.user) haptic('selection')
    }
    const toH = stops[index].height
    const fromH = currentH()
    updateInteractivity(toH)
    const dist = Math.abs(toH - fromH)
    // Speed along the direction of travel: growing = moving up = negative screen velocity.
    const speed = Math.max(0, toH > fromH ? -(opts.velocity ?? 0) : opts.velocity ?? 0)
    const duration = reduced() ? DUR_REDUCED.fast : opts.duration ?? snapDuration(dist, speed, 300)
    const curve = reduced() ? EASE.out : snapCurve(dist, speed, duration)
    if (dist < 0.5) {
        settle(toH)
        emit('snap', stops[index].id)
        return
    }
    if (!(await animateTo(toH, duration, curve))) return
    settle(toH)
    emit('snap', stops[index].id)
}

function initialIndex(): number {
    const want = detentModel.value ?? props.detents[0]
    const at = stops.findIndex((d) => d.id === want)
    return at >= 0 ? at : 0
}

// ── show / hide ─────────────────────────────────────────────────────────────────────────────────────────────
async function show() {
    if (phase.value === 'open' || phase.value === 'entering') return
    const reentering = phase.value === 'leaving'
    phase.value = 'entering'
    if (!reentering) {
        prevFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
        rendered.value = true
        await nextTick()
        if (!props.open || phase.value !== 'entering') return
    }
    attach()

    if (isDialog.value) {
        animToken++
        void getComputedStyle(panelEl.value!).opacity
        dialogIn.value = true
        scrimActive.value = true
        phase.value = 'open'
        focusIn()
        return
    }

    measure()
    writeSpacer()
    index = initialIndex()
    syncModel()
    const h = stops[index].height
    updateInteractivity(h)
    focusIn()

    if (reduced()) {
        animToken++
        settle(h)
        const p = panelEl.value!
        p.style.opacity = '0'
        if (scrimEl.value) scrimEl.value.style.opacity = '0'
        void getComputedStyle(p).opacity
        p.style.transition = `opacity ${DUR_REDUCED.push}ms ${EASE.out}`
        if (scrimEl.value) scrimEl.value.style.transition = `opacity ${DUR_REDUCED.push}ms ${EASE.out}`
        p.style.opacity = ''
        if (scrimEl.value) scrimEl.value.style.opacity = String(scrimOpacity(h, scrimRange(stops, props.scrim)))
        phase.value = 'open'
        emit('snap', stops[index].id)
        return
    }

    if (!reentering) {
        // Start just below the screen.
        setTransition(null)
        writeFrame(h, h + OFFSCREEN_EXTRA)
    }
    const ok = await animateTo(h, DUR.push, EASE.ios)
    if (phase.value !== 'entering') return // hide() took over
    // Open even when a drag caught the sheet mid-presentation (the drag settles it).
    phase.value = 'open'
    if (ok) {
        settle(h)
        emit('snap', stops[index].id)
    }
    if (pendingRelayout) scheduleRelayout()
}

async function hide() {
    if (phase.value === 'closed' || phase.value === 'leaving') return
    phase.value = 'leaving'
    if (!panelEl.value) {
        // Closed again before the first frame: nothing to animate.
        animToken++
        phase.value = 'closed'
        rendered.value = false
        return
    }
    detach()
    cancelReveal?.()
    dragging.value = false
    drag = null
    touch = null
    const velocity = dismissVelocity
    dismissVelocity = 0

    if (isDialog.value) {
        const token = ++animToken
        dialogIn.value = false
        scrimActive.value = false
        await new Promise((r) => setTimeout(r, DUR.fast + 40))
        if (token !== animToken) return
    } else if (reduced()) {
        const token = ++animToken
        const p = panelEl.value
        if (p) {
            p.style.transition = `opacity ${DUR_REDUCED.pop}ms ${EASE.out}`
            p.style.opacity = '0'
        }
        if (scrimEl.value) {
            scrimEl.value.style.transition = `opacity ${DUR_REDUCED.pop}ms ${EASE.out}`
            scrimEl.value.style.opacity = '0'
        }
        scrimActive.value = false
        await new Promise((r) => setTimeout(r, DUR_REDUCED.pop + 40))
        if (token !== animToken) return
    } else {
        scrimActive.value = false
        const from = currentH()
        const dist = from + OFFSCREEN_EXTRA
        const speed = Math.max(0, velocity)
        const duration = snapDuration(dist, speed, DUR.pop, 160)
        if (!(await animateTo(0, duration, speed > 0 ? snapCurve(dist, speed, duration) : EASE.ios))) return
    }
    if (phase.value !== 'leaving') return
    restoreFocus()
    phase.value = 'closed'
    rendered.value = false
    panelH = 0
    offset = 0
}

watch(() => props.open, (open) => (open ? void show() : void hide()), { immediate: true })

/** The creator asked to close (drag, scrim, back, Escape). The parent owns `open`. */
function requestClose(velocity = 0) {
    if (!props.dismissible) {
        void snapToIndex(0)
        return
    }
    dismissVelocity = velocity
    emit('close')
    // A parent that keeps it open gets the sheet back at its detent.
    void nextTick(() => {
        if (props.open && phase.value === 'open') {
            dismissVelocity = 0
            void snapToIndex(index)
        }
    })
}

// ── listeners while shown ───────────────────────────────────────────────────────────────────────────────────
function onBack(): boolean {
    if (phase.value !== 'open' && phase.value !== 'entering') return false
    if (props.dismissible) {
        requestClose()
        return true
    }
    if (index > 0 && !isDialog.value) {
        void snapToIndex(index - 1)
        return true
    }
    return false
}

function attach() {
    offBack?.()
    offBack = pushBackHandler(onBack)
    window.addEventListener('resize', scheduleRelayout, { passive: true })
    if (typeof ResizeObserver !== 'undefined') {
        ro?.disconnect()
        ro = new ResizeObserver(scheduleRelayout)
        for (const el of [headEl.value, contentEl.value, footEl.value]) if (el) ro.observe(el)
    }
}

function detach() {
    offBack?.()
    offBack = null
    window.removeEventListener('resize', scheduleRelayout)
    ro?.disconnect()
    ro = null
    if (relayoutFrame) cancelAnimationFrame(relayoutFrame)
    relayoutFrame = 0
}

function scheduleRelayout() {
    if (relayoutFrame) return
    relayoutFrame = requestAnimationFrame(() => {
        relayoutFrame = 0
        relayout()
    })
}

/** Content, viewport or keyboard changed: re-measure and move to the current detent's new height. */
function relayout() {
    if (!rendered.value || isDialog.value || phase.value === 'leaving' || phase.value === 'closed') return
    if (drag || phase.value === 'entering') {
        pendingRelayout = true
        return
    }
    pendingRelayout = false
    const target = stops[index]?.height
    measure()
    writeSpacer()
    if (!stops.length) return
    const toH = stops[index].height
    if (animating && target !== undefined && Math.abs(target - toH) < 0.5) {
        writeFrame(panelH, offset)
        return
    }
    if (Math.abs(toH - currentH()) < 0.5 && !animating) {
        // Same height: only the footer may need the new keyboard lift. Leave the panel's own transition alone
        // (it may be mid-fade).
        if (footEl.value) footEl.value.style.transition = `transform ${DUR.pop}ms ${EASE.ios}`
        writeFrame(panelH, offset)
        return
    }
    void snapToIndex(index, { duration: DUR.pop })
}

// A pill dropping into (or leaving) the top island moves the large detent.
watch(topIslandHeight, () => {
    if (rendered.value) scheduleRelayout()
})

watch(keyboard.height, () => {
    if (props.keyboardAware && rendered.value) scheduleRelayout()
})
watch(() => props.detents.join('|'), scheduleRelayout)
watch(detentModel, (id) => {
    if (id === undefined || phase.value !== 'open' || stops[index]?.id === id) return
    const at = stops.findIndex((d) => d.id === id)
    if (at >= 0) void snapToIndex(at)
})
watch(isDialog, async () => {
    if (!rendered.value || phase.value === 'leaving') return
    // Crossed the 640px breakpoint while open: reset to the other presentation.
    animToken++
    animating = false
    clearInline()
    panelH = 0
    offset = 0
    await nextTick()
    if (isDialog.value) {
        dialogIn.value = true
        scrimActive.value = true
    } else {
        measure()
        writeSpacer()
        settle(stops[index].height)
        updateInteractivity(stops[index].height)
    }
})

// ── dragging (shared by the header and the body) ───────────────────────────────────────────────────────────
function bodyScrollable(): boolean {
    const b = bodyEl.value
    return !!b && !props.noBodyScroll && b.scrollHeight > b.clientHeight + 1
}

function beginDrag(y: number, t: number, fromBody: boolean) {
    interrupt()
    const h = visibleH()
    const H = Math.max(stops[topIndex()].height, Math.round(h))
    setTransition(null)
    setMoving(true)
    writeFrame(H, H - h)
    drag = { startY: y, startH: h, H, fromBody, scrollStart: bodyEl.value?.scrollTop ?? 0, scrolled: false, tracker: new VelocityTracker() }
    drag.tracker.add(t, y)
    dragging.value = true
    // Dragging the sheet puts the keyboard away, like iOS.
    const active = document.activeElement
    if (keyboard.visible.value && active instanceof HTMLElement && panelEl.value?.contains(active)) active.blur()
}

function moveDrag(y: number, t: number) {
    if (!drag) return
    drag.tracker.add(t, y)
    pendingY = y
    if (!dragFrame) dragFrame = requestAnimationFrame(applyDrag)
}

function applyDrag() {
    dragFrame = 0
    if (!drag) return
    const raw = drag.startH - (pendingY - drag.startY)
    const top = stops[topIndex()].height
    let h: number
    const body = bodyEl.value
    if (drag.fromBody && body && raw > top && bodyScrollable()) {
        // Past the top detent a body drag scrolls the content instead, and gives it back on the way down.
        h = top
        body.scrollTop = drag.scrollStart + (raw - top)
        drag.scrolled = true
    } else {
        h = dragHeight(raw, stops[0].height, top, { viewport: metrics.viewport, dismissible: props.dismissible })
        if (drag.scrolled && body) {
            body.scrollTop = drag.scrollStart
            drag.scrolled = false
        }
    }
    writeFrame(drag.H, drag.H - h)
}

function endDrag(t: number, cancelled: boolean) {
    if (!drag) return
    if (dragFrame) cancelAnimationFrame(dragFrame)
    applyDrag()
    const velocity = cancelled ? 0 : drag.tracker.velocity(t)
    const h = visibleH()
    const startIndex = index
    const scrolled = drag.scrolled
    drag = null
    // Ended while scrolling the content: let it carry on a little, like a flick.
    if (scrolled && velocity < -0.2 && !reduced()) bodyEl.value?.scrollBy({ top: Math.min(1200, -velocity * 260), behavior: 'smooth' })
    dragging.value = false
    const target = pickSnap({ heights: stops.map((d) => d.height), current: h, velocity, dismissible: props.dismissible })
    if (target === -1) {
        requestClose(velocity)
        return
    }
    void snapToIndex(target, { velocity, user: target !== startIndex })
    if (pendingRelayout) scheduleRelayout()
}

// Header and grabber: pointer events (touch, pen and mouse). The drag starts after 4px so taps on header
// buttons still work; the click that would follow a drag is swallowed.
function onHeadPointerDown(e: PointerEvent) {
    if (isDialog.value || !e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return
    if (phase.value !== 'open' && phase.value !== 'entering') return
    headPtr = { id: e.pointerId, y: e.clientY, t: e.timeStamp, started: false }
    window.addEventListener('pointermove', onHeadPointerMove)
    window.addEventListener('pointerup', onHeadPointerUp)
    window.addEventListener('pointercancel', onHeadPointerUp)
}

function onHeadPointerMove(e: PointerEvent) {
    if (!headPtr || e.pointerId !== headPtr.id) return
    if (!headPtr.started) {
        if (Math.abs(e.clientY - headPtr.y) < 4) return
        headPtr.started = true
        beginDrag(headPtr.y, headPtr.t, false)
    }
    moveDrag(e.clientY, e.timeStamp)
}

function onHeadPointerUp(e: PointerEvent) {
    if (!headPtr || e.pointerId !== headPtr.id) return
    window.removeEventListener('pointermove', onHeadPointerMove)
    window.removeEventListener('pointerup', onHeadPointerUp)
    window.removeEventListener('pointercancel', onHeadPointerUp)
    if (headPtr.started) {
        endDrag(e.timeStamp, e.type === 'pointercancel')
        swallowNextClick()
    }
    headPtr = null
}

function swallowNextClick() {
    const p = panelEl.value
    if (!p) return
    const stop = (ev: Event) => {
        ev.preventDefault()
        ev.stopPropagation()
    }
    p.addEventListener('click', stop, { capture: true, once: true })
    setTimeout(() => p.removeEventListener('click', stop, { capture: true }), 400)
}

// Body: touch events, because only a non-passive touchmove can still take a gesture away from native
// scrolling. The decision is made on the first few px of movement.
function findTouch(list: TouchList, id: number): Touch | null {
    for (let i = 0; i < list.length; i++) if (list[i].identifier === id) return list[i]
    return null
}

/** A scroller between the touch target and the body that can still scroll this way keeps the gesture. */
function nestedScrolls(target: EventTarget | null, dy: number): boolean {
    const body = bodyEl.value
    if (target instanceof Element && target.closest('[data-sheet-no-drag]')) return true
    for (let n = target instanceof Element ? target : null; n && n !== body; n = n.parentElement) {
        if (!(n instanceof HTMLElement)) continue
        if (n.scrollHeight <= n.clientHeight + 1) continue
        const oy = getComputedStyle(n).overflowY
        if (oy !== 'auto' && oy !== 'scroll') continue
        if (dy > 0 ? n.scrollTop > 0 : n.scrollTop + n.clientHeight < n.scrollHeight - 1) return true
    }
    return false
}

function bodyDragAllowed(dy: number, target: EventTarget | null): boolean {
    if (nestedScrolls(target, dy)) return false
    const b = bodyEl.value
    if (!b) return false
    if (dy > 0) return props.noBodyScroll || b.scrollTop <= 0
    if (index < topIndex()) return true // expand before scrolling
    return !bodyScrollable()
}

function onBodyTouchStart(e: TouchEvent) {
    if (isDialog.value || (phase.value !== 'open' && phase.value !== 'entering')) return
    if (e.touches.length > 1) {
        if (touch?.mode === 'drag') endDrag(e.timeStamp, true)
        touch = null
        return
    }
    const t = e.changedTouches[0]
    touch = { id: t.identifier, x: t.clientX, y: t.clientY, t: e.timeStamp, mode: 'pending' }
}

function onBodyTouchMove(e: TouchEvent) {
    if (!touch || touch.mode === 'scroll') return
    const t = findTouch(e.changedTouches, touch.id)
    if (!t) return
    if (touch.mode === 'pending') {
        const dx = t.clientX - touch.x
        const dy = t.clientY - touch.y
        if (Math.abs(dx) + Math.abs(dy) < 3) return
        if (Math.abs(dx) > Math.abs(dy) || !e.cancelable || !bodyDragAllowed(dy, e.target)) {
            touch.mode = 'scroll'
            return
        }
        touch.mode = 'drag'
        beginDrag(touch.y, touch.t, true)
    }
    if (e.cancelable) e.preventDefault()
    moveDrag(t.clientY, e.timeStamp)
}

function onBodyTouchEnd(e: TouchEvent) {
    if (!touch) return
    if (!findTouch(e.changedTouches, touch.id)) return
    if (touch.mode === 'drag') endDrag(e.timeStamp, e.type === 'touchcancel')
    touch = null
}

// ── taps, keys, focus ───────────────────────────────────────────────────────────────────────────────────────
function onScrimTap() {
    if (phase.value !== 'open') return
    if (props.dismissible) requestClose()
    else if (index > 0) void snapToIndex(0)
}

function onGrabberTap() {
    if (stops.length < 2 || phase.value !== 'open') return
    void snapToIndex(index >= topIndex() ? 0 : index + 1)
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable]:not([contenteditable="false"])'

function onKeydown(e: KeyboardEvent) {
    if (e.key !== 'Tab' || !(scrimActive.value || isDialog.value)) return
    const p = panelEl.value
    if (!p) return
    // Only what Tab can actually reach: not the tabindex=-1 grabber, nothing in an inert (pager) page.
    const items = [...p.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.tabIndex >= 0 && !el.closest('[inert]') && (el.offsetParent !== null || el === document.activeElement),
    )
    if (!items.length) {
        e.preventDefault()
        return
    }
    const first = items[0]
    const last = items[items.length - 1]
    const active = document.activeElement
    if (e.shiftKey && (active === first || active === p)) {
        e.preventDefault()
        last.focus()
    } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
    }
}

function onFocusIn(e: FocusEvent) {
    if (!props.keyboardAware || isDialog.value || !isTextEntry(e.target as Element)) return
    if (index < topIndex() && phase.value === 'open') void snapToIndex(topIndex())
    cancelReveal?.()
    cancelReveal = keepAboveKeyboard(e.target as HTMLElement)
}

function focusIn() {
    // Modal only: a peeking sheet leaves focus (and the keyboard) where it was.
    if (!scrimActive.value && !isDialog.value) return
    panelEl.value?.focus({ preventScroll: true })
}

function restoreFocus() {
    const el = prevFocus
    prevFocus = null
    const active = document.activeElement
    const focusInside = !!active && !!rootEl.value?.contains(active)
    if (!el || !el.isConnected) {
        if (focusInside) (active as HTMLElement).blur()
        return
    }
    if (!focusInside && active && active !== document.body) return // focus moved on purpose
    // Don't bring the keyboard back on a phone just because a sheet closed.
    if (isTextEntry(el) && isTouchPrimary()) {
        if (focusInside) (active as HTMLElement).blur()
        return
    }
    el.focus({ preventScroll: true })
}

onBeforeUnmount(() => {
    detach()
    cancelReveal?.()
    animToken++
    if (rendered.value) restoreFocus()
    window.removeEventListener('pointermove', onHeadPointerMove)
    window.removeEventListener('pointerup', onHeadPointerUp)
    window.removeEventListener('pointercancel', onHeadPointerUp)
})

defineExpose({
    /** Animate to a detent (by id). Before opening, sets the detent it opens at. */
    snapTo(id: DetentId) {
        const at = stops.findIndex((d) => d.id === id)
        if (phase.value === 'open' && at >= 0) void snapToIndex(at)
        else detentModel.value = id
    },
})
</script>

<template>
    <Teleport to="body">
        <div
            v-if="rendered"
            ref="rootEl"
            class="ds-root"
            :class="[`is-layer-${layer}`, { 'is-dialog': isDialog, 'is-in': dialogIn, 'is-dragging': dragging }]"
            :data-phase="phase"
        >
            <div ref="scrimEl" class="ds-scrim" :class="{ 'is-active': scrimActive }" aria-hidden="true" @click="onScrimTap" @wheel.prevent />
            <div
                ref="panelEl"
                v-bind="$attrs"
                class="ds-panel"
                role="dialog"
                :aria-modal="scrimActive || isDialog ? 'true' : undefined"
                :aria-label="ariaLabel || (title ? undefined : 'Sheet')"
                :aria-labelledby="!ariaLabel && title ? titleId : undefined"
                tabindex="-1"
                @keydown="onKeydown"
                @focusin="onFocusIn"
            >
                <div ref="headEl" class="ds-head" @pointerdown="onHeadPointerDown">
                    <button v-if="handle && !isDialog" type="button" class="ds-grabber" tabindex="-1" aria-label="Resize" @click="onGrabberTap">
                        <span />
                    </button>
                    <slot name="header">
                        <!-- The one sheet header: the title on the left, a 36px circle close (phones, dismissible); dialogs
                             keep their centred title. Editing sheets bring their own (Cancel / title / Done). -->
                        <div v-if="title || isDialog" class="ds-title-row" :class="{ 'is-sheet': !isDialog }">
                            <h2 v-if="title" :id="titleId" class="ds-title">{{ title }}</h2>
                            <button v-if="dismissible" type="button" class="ds-close" aria-label="Close" @pointerdown.stop @click="requestClose()">
                                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7" /></svg>
                            </button>
                        </div>
                    </slot>
                </div>
                <div
                    ref="bodyEl"
                    class="ds-body"
                    :class="[bodyClass, { 'is-fixed': noBodyScroll }]"
                    @touchstart.passive="onBodyTouchStart"
                    @touchmove="onBodyTouchMove"
                    @touchend="onBodyTouchEnd"
                    @touchcancel="onBodyTouchEnd"
                >
                    <div ref="contentEl" class="ds-content"><slot /></div>
                    <div ref="spacerEl" class="ds-spacer" aria-hidden="true" />
                </div>
                <div v-if="$slots.footer" ref="footEl" class="ds-foot"><slot name="footer" /></div>
            </div>
            <div ref="probeEl" class="ds-probe" aria-hidden="true" />
        </div>
    </Teleport>
</template>

<style scoped>
.ds-root {
    --ds-bg-default: #ffffff;
    --ds-scrim-color: rgb(10 10 10 / 0.3);
    --ds-grabber: rgb(15 15 15 / 0.18);
    position: fixed;
    inset: 0;
    z-index: var(--z-sheet, 10051);
    pointer-events: none;
}
.ds-root.is-layer-modal {
    z-index: var(--z-sheet-modal, 10070);
}
html.dark .ds-root {
    --ds-bg-default: var(--lh-surface, #1c1917);
    --ds-scrim-color: var(--lh-scrim, rgb(0 0 0 / 0.64));
    --ds-grabber: rgb(255 255 255 / 0.22);
}
.ds-root.is-dragging {
    -webkit-user-select: none;
    user-select: none;
}

.ds-scrim {
    position: absolute;
    inset: 0;
    background: var(--ds-scrim-color);
    opacity: 0;
    pointer-events: none;
    touch-action: none;
}
.ds-scrim.is-active {
    pointer-events: auto;
}

.ds-panel {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    pointer-events: auto;
    color: var(--lh-text-1, #242424);
    background: var(--ds-bg, var(--ds-bg-default));
    border-radius: var(--ds-radius, 22px) var(--ds-radius, 22px) 0 0;
    box-shadow: var(--ds-shadow, 0 -1px 0 rgb(15 15 15 / 0.04), 0 -12px 40px -14px rgb(15 15 15 / 0.24));
    outline: none;
}
html.dark .ds-panel {
    box-shadow: var(--ds-shadow, 0 0 0 1px rgb(255 255 255 / 0.06), 0 -16px 48px -12px rgb(0 0 0 / 0.7));
}
/* Fills the gap under the panel when it's rubber-banded above its top detent. */
.ds-panel::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% - 1px);
    height: 50vh;
    background: inherit;
    pointer-events: none;
}
.ds-panel[data-moving] {
    will-change: transform;
}

.ds-head {
    flex-shrink: 0;
    touch-action: none;
    -webkit-user-select: none;
    user-select: none;
    cursor: grab;
}
.ds-root.is-dragging .ds-head {
    cursor: grabbing;
}
.ds-grabber {
    display: flex;
    justify-content: center;
    width: 100%;
    height: 20px;
    padding-top: 6px;
    background: none;
    border: 0;
    cursor: inherit;
    -webkit-tap-highlight-color: transparent;
}
.ds-grabber span {
    width: 36px;
    height: 5px;
    border-radius: 3px;
    background: var(--ds-grabber);
}
.ds-title-row {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 56px 8px;
}
.ds-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.25;
    text-align: center;
    color: var(--lh-text-1, #242424);
}
/* Phones: the same recipe as Modal's and the approval / question sheets' headers. */
.ds-title-row.is-sheet {
    justify-content: flex-start;
    align-items: flex-start;
    gap: 12px;
    min-height: 0;
    padding: 2px 12px 10px 20px;
}
.ds-title-row.is-sheet .ds-title {
    flex: 1;
    min-width: 0;
    padding-top: 6px;
    font-size: 17px;
    letter-spacing: -0.01em;
    text-align: left;
}
.ds-title-row.is-sheet .ds-close {
    position: static;
    flex-shrink: 0;
    margin: -4px 0 0 auto;
}

.ds-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}
.ds-body.is-fixed {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.ds-body.is-fixed > .ds-content {
    flex: 1 1 auto;
    min-height: 0;
}
.ds-spacer {
    flex-shrink: 0;
}

.ds-foot {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background: var(--ds-bg, var(--ds-bg-default));
}

.ds-probe {
    position: absolute;
    visibility: hidden;
    pointer-events: none;
    /* The top island (call pill, offline pill) counts as top inset: a large sheet's header stays under it. */
    padding-top: calc(env(safe-area-inset-top, 0px) + var(--lh-top-island, 0px));
    padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* ── Dialog presentation (presentation="auto" at ≥ 640px) ── */
.ds-root.is-dialog {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
}
.ds-root.is-dialog .ds-scrim {
    transition: opacity var(--lh-dur-base, 240ms) var(--lh-ease-out, ease-out);
}
.ds-root.is-dialog.is-in .ds-scrim {
    opacity: 1;
}
.ds-root.is-dialog .ds-panel {
    position: relative;
    left: auto;
    right: auto;
    bottom: auto;
    width: min(var(--ds-dialog-width, 560px), 100%);
    max-height: min(85vh, 760px);
    border-radius: var(--ds-dialog-radius, 18px);
    box-shadow: var(--agent-float-shadow, 0 24px 64px -20px rgb(15 15 15 / 0.35));
    overflow: hidden;
    opacity: 0;
    transform: translateY(8px) scale(0.97);
    transition:
        opacity var(--lh-dur-fast, 150ms) var(--lh-ease-out, ease-out),
        transform var(--lh-dur-base, 240ms) var(--lh-ease-out, ease-out);
}
.ds-root.is-dialog.is-in .ds-panel {
    opacity: 1;
    transform: none;
}
.ds-root.is-dialog .ds-panel::after {
    display: none;
}
.ds-root.is-dialog .ds-head {
    touch-action: auto;
    cursor: default;
}
.ds-root.is-dialog .ds-title-row {
    min-height: 56px;
    padding-top: 8px;
}
.ds-root.is-dialog .ds-spacer {
    display: none;
}
.ds-root.is-dialog .ds-foot {
    position: relative;
    padding-bottom: 0;
}
.ds-close {
    position: absolute;
    right: 12px;
    top: 50%;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    margin-top: -14px;
    border: 0;
    border-radius: 999px;
    background: var(--agent-hover, rgb(15 15 15 / 0.04));
    color: var(--lh-text-2, #525252);
    cursor: pointer;
}
html.dark .ds-close {
    background: var(--agent-hover, rgb(255 255 255 / 0.08));
}
.ds-close svg {
    width: 14px;
    height: 14px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.6;
    stroke-linecap: round;
}
@media (hover: hover) and (pointer: fine) {
    .ds-close:hover {
        background: var(--agent-press, rgb(15 15 15 / 0.07));
        color: var(--lh-text-1, #242424);
    }
}
</style>
